/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import * as React from "react";
import { devices, fontFamilies } from "components/shared/Styles";
import { Analytics, AnalyticsEvent } from "constants/Analytics";
import crimeTemplate1x from "images/home/landing/Templates/crime-template-cover@1x.png";
import crimeTemplate2x from "images/home/landing/Templates/crime-template-cover@2x.png";
import analysisTemplate1x from "images/home/landing/Templates/analysis-template-cover@1x.png";
import analysisTemplate2x from "images/home/landing/Templates/analysis-template-cover@2x.png";
import trendTemplate1x from "images/home/landing/Templates/trend-template-cover@1x.png";
import trendTemplate2x from "images/home/landing/Templates/trend-template-cover@2x.png";
import previewPlayIcon from "images/home/landing/Templates/play.svg";
import previewPauseIcon from "images/home/landing/Templates/pause.svg";
import { useCallback, useState } from "react";
import { webComponentShim } from "components/shared/WebComponentShim";

interface TemplateCardProps {
  title: string;
  coverImage1x: string;
  coverImage2x: string;
  audioSampleUrl: string;
  isPlaying: boolean;
  onPreviewClicked: (audioSampleUrl: string) => void;
  onOpenProjectClicked: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  title,
  coverImage1x,
  coverImage2x,
  audioSampleUrl,
  isPlaying,
  onPreviewClicked,
  onOpenProjectClicked,
}) => {
  const previewIcon = isPlaying ? previewPauseIcon : previewPlayIcon;
  const ariaLabel = isPlaying
    ? I18n.t("landing.template_lineup.pause_preview")
    : I18n.t("landing.template_lineup.preview_project");

  return (
    <TemplateCardContainer data-testid="template-card">
      <TemplateTitle>{title}</TemplateTitle>
      <ButtonsContainer>
        <PreviewButton
          onClick={() => {
            onPreviewClicked(audioSampleUrl);
          }}
          aria-label={ariaLabel}
        >
          <PreviewIcon
            slot="icon"
            src={previewIcon}
            alt={I18n.t("landing.template_lineup.preview_project")}
          />
        </PreviewButton>
        <OpenProjectButton onClick={onOpenProjectClicked}>
          {I18n.t("landing.template_lineup.open_project")}
        </OpenProjectButton>
      </ButtonsContainer>
      <CoverArt
        srcSet={`${coverImage1x} 1x, ${coverImage2x} 2x`}
        alt={I18n.t("landing.template_lineup.cover_art", { title: title })}
      />
    </TemplateCardContainer>
  );
};

export const TemplateLineup: React.FC = () => {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudioSampleUrl, setCurrentAudioSampleUrl] = useState<
    string | null
  >(null);

  const templates = [
    {
      title: I18n.t("landing.template_lineup.templates.true_crime.title"),
      coverImage1x: crimeTemplate1x,
      coverImage2x: crimeTemplate2x,
      audioSampleUrl: `${process.env.CDN_ROOT}/template-lineup-previews/Crime_Time_(preview).mp3`,
      templateUrl:
        "https://podcast.adobe.com/templates/cc40fa24-afac-404c-8503-a781028b739f",
      analyticsEvent: AnalyticsEvent.HOME_TEMPLATE_OPEN_PROJECT_CLICKED_1,
    },
    {
      title: I18n.t("landing.template_lineup.templates.class_lecture.title"),
      coverImage1x: analysisTemplate1x,
      coverImage2x: analysisTemplate2x,
      audioSampleUrl: `${process.env.CDN_ROOT}/template-lineup-previews/The_Art_of_Analysis_(preview).mp3`,
      templateUrl:
        "https://podcast.adobe.com/templates/f77a289e-3f03-45b2-9d0d-4a367617574d",
      analyticsEvent: AnalyticsEvent.HOME_TEMPLATE_OPEN_PROJECT_CLICKED_2,
    },
    {
      title: I18n.t("landing.template_lineup.templates.fashion.title"),
      coverImage1x: trendTemplate1x,
      coverImage2x: trendTemplate2x,
      audioSampleUrl: `${process.env.CDN_ROOT}/template-lineup-previews/Trend_Break_(preview).mp3`,
      templateUrl:
        "https://podcast.adobe.com/templates/2e4e17a7-c6b0-46f1-a63e-b07e6dce53e3",
      analyticsEvent: AnalyticsEvent.HOME_TEMPLATE_OPEN_PROJECT_CLICKED_3,
    },
  ];

  const onPreviewClicked = useCallback(
    (clickedAudioSampleUrl: string) => {
      if (!audioRef.current) {
        return;
      }

      if (currentAudioSampleUrl === clickedAudioSampleUrl) {
        // User clicked on the same template
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
      } else {
        // User clicked on a different template
        audioRef.current.src = clickedAudioSampleUrl;
        audioRef.current.play();
        setIsPlaying(true);
        setCurrentAudioSampleUrl(clickedAudioSampleUrl);
      }
    },
    [currentAudioSampleUrl, isPlaying]
  );

  return (
    <TemplateLineupContainer>
      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />
      <TemplateLineupTitle>
        {I18n.t("landing.template_lineup.title")}
      </TemplateLineupTitle>
      <TemplateLineupSubtitle>
        {I18n.t("landing.template_lineup.subtitle")}
      </TemplateLineupSubtitle>
      <TemplateRow>
        {templates.map((template, index) => (
          <TemplateCard
            title={template.title}
            coverImage1x={template.coverImage1x}
            coverImage2x={template.coverImage2x}
            audioSampleUrl={template.audioSampleUrl}
            onOpenProjectClicked={() => {
              Analytics.instance.logEvent(template.analyticsEvent, {
                templateName: template.title,
                templateUrl: template.templateUrl,
              });
              window.open(template.templateUrl, "_blank");
            }}
            onPreviewClicked={onPreviewClicked}
            isPlaying={
              currentAudioSampleUrl === template.audioSampleUrl && isPlaying
            }
            key={index}
          />
        ))}
      </TemplateRow>
    </TemplateLineupContainer>
  );
};

const TemplateLineupContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 42px;
  padding-top: 92px;
  width: calc(100% - 212px);
  margin: auto;

  ${devices.small} {
    width: calc(100% - 48px);
  }
`;

const TemplateLineupTitle = styled.h2`
  font: normal 900 48px/108% ${fontFamilies};
  color: black;
  text-align: center;
  margin: 0 0 16px 0;
`;

const TemplateLineupSubtitle = styled.p`
  font: normal 700 24px/100% ${fontFamilies};
  color: black;
  text-align: center;
  margin: 0 0 72px 0;
`;

const TemplateRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 32px;
  max-width: fit-content;
  width: 100%;

  ${devices.medium} {
    flex-direction: column;
    align-items: center;
  }

  ${devices.small} {
    flex-direction: column;
    align-items: center;
    max-width: 100%;
  }
`;

const CoverArt = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const TemplateCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: white;
  border-radius: 32px;
  overflow: hidden;
  box-shadow: var(--phonos-box-shadow);
  padding: 32px;
  max-width: fit-content;
  margin-bottom: auto;
  box-sizing: border-box;

  ${devices.medium} {
    max-width: 100%;
    width: 600px;
  }

  ${devices.small} {
    max-width: 600px;
    width: 100%;
  }
`;

const TemplateTitle = styled.h3`
  font: normal 900 32px/39px ${fontFamilies};
  color: black;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 304px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const PreviewButton = styled(webComponentShim("sp-button")).attrs({
  size: "l",
  variant: "primary",
  "icon-only": true,
})``;

const OpenProjectButton = styled(webComponentShim("sp-button")).attrs({
  size: "l",
  variant: "secondary",
})``;

const PreviewIcon = styled.img`
  width: 20px;
  height: 20px;
`;
